import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, inject } from '@angular/core';
import { concat } from 'rxjs';

import { EnvironmentService } from './environment.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandlerService implements ErrorHandler {
  private environmentService = inject(EnvironmentService);
  private http = inject(HttpClient);
  private lastError: string = '';

  handleError(error: HttpErrorResponse) {
    if (!this.environmentService.environment.production) {
      if (error?.statusText === 'Unknown Error') {
        console.error('An error occurred:', error?.message, error?.url);
      } else {
        console.error('An error occurred:', error);
      }
    }
    const rawPayload = (error as any)?.error ?? error;

    let backendPayload: any = rawPayload;
    if (typeof backendPayload === 'string') {
      try {
        backendPayload = JSON.parse(backendPayload);
      } catch {
        backendPayload = null;
      }
    }

    const backendErrors: any[] = Array.isArray(backendPayload?.errors)
      ? backendPayload.errors
      : Array.isArray(backendPayload)
        ? backendPayload
        : [];

    if (!backendErrors.length) {
      return;
    }

    const serializedErrors = JSON.stringify(backendErrors);
    if (serializedErrors === this.lastError) {
      return;
    }
    this.lastError = serializedErrors;

    const hasTraceback = backendErrors.some(
      (err: any) => Array.isArray(err?.traceback) && err.traceback.length > 0,
    );
    if (hasTraceback) {
      this.sendErrorToDiscord(backendPayload ?? backendErrors);
    }
  }

  private sendErrorToDiscord(payload: any): void {
    const webhookUrl = this.environmentService.environment?.discordWebhookUrl;
    if (!webhookUrl) {
      return;
    }
    if (this.environmentService.environment?.local) {
      return;
    }
    const envName =
      this.environmentService.environment?.envName ??
      (this.environmentService.environment?.production ? 'production' : 'development');

    const json = JSON.stringify(payload, null, 2);
    const maxContentLength = 2000;
    const headerBase = `Environment: ${envName}`;
    const codePrefix = '```json\n';
    const codeSuffix = '\n```';

    const contents: string[] = [];
    let offset = 0;
    let partIndex = 1;

    while (offset < json.length) {
      const partInfo = json.length > maxContentLength ? ` (part ${partIndex})` : '';
      const header = `${headerBase}${partInfo}\n${codePrefix}`;
      const availableForJson = maxContentLength - header.length - codeSuffix.length;

      if (availableForJson <= 0) {
        break;
      }

      const chunk = json.slice(offset, offset + availableForJson);
      contents.push(header + chunk + codeSuffix);

      offset += availableForJson;
      partIndex += 1;
    }

    if (!contents.length) {
      contents.push(`${headerBase}\n${codePrefix}${codeSuffix}`);
    }

    const requests = contents.map((content) => this.http.post(webhookUrl, { content }));

    concat(...requests).subscribe({
      next: (responses) => {},
      error: (err) => {},
    });
  }
}
